import React, { useState, useEffect } from "react";

import { LabService, type LabAnalytics as ServiceLabAnalytics, type Lab as ServiceLab } from "../services/labService";
import { normalizeLabType } from "../types/lab";
import { Alert } from "./Alert";

interface LabSummary {
    total_labs: number;
    total_members: number;
    total_fte: number;
    average_utilization: number;
}

interface LabTypeData {
    count: number;
    total_members: number;
    total_fte: number;
}

type UiLabDetail = {
    id: string;
    name: string;
    type: string;
    member_count: number;
    total_fte: number;
    utilization: number;
    champion_email: string | null;
};

type CapacityIssue = ServiceLabAnalytics["capacity_issues"]["over_capacity"][number];
type UnderUtilizedLab = ServiceLabAnalytics["capacity_issues"]["under_utilized"][number];

interface AnalyticsData {
    summary: LabSummary;
    lab_details: UiLabDetail[];
    lab_type_distribution: Record<string, LabTypeData>;
    capacity_issues: {
        over_capacity: CapacityIssue[];
        under_utilized: UnderUtilizedLab[];
    };
}

export const LabAnalytics: React.FC = () => {
    const [analytics, setAnalytics] = useState<AnalyticsData | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchAnalytics = async (): Promise<void> => {
            try {
                setIsLoading(true);
                const data: ServiceLabAnalytics = await LabService.getLabAnalytics();
                const normalizedData: AnalyticsData = {
                    summary: {
                        total_labs: data.summary.total_labs,
                        total_members: data.summary.total_members,
                        total_fte: data.summary.total_fte,
                        average_utilization: data.summary.average_utilization,
                    },
                    lab_details: data.lab_details.map((lab: ServiceLab) => {
                        const rawUtilization = 'utilization' in lab
                            ? (lab as { utilization?: number | string }).utilization
                            : undefined;
                        const numericUtilization = typeof rawUtilization === "string"
                            ? parseFloat(rawUtilization)
                            : rawUtilization;

                        return {
                            id: lab.id,
                            name: lab.name,
                            type: normalizeLabType(lab.type),
                            member_count: lab.member_count,
                            total_fte: lab.total_fte,
                            utilization: Number.isFinite(numericUtilization) ? Number(numericUtilization) : 0,
                            champion_email: lab.champion_email ?? null,
                        } satisfies UiLabDetail;
                    }),
                    lab_type_distribution: data.lab_type_distribution,
                    capacity_issues: data.capacity_issues,
                };
                setAnalytics(normalizedData);
            } catch (err) {
                const message = err instanceof Error ? err.message : "Failed to load analytics.";
                setError(message);
            } finally {
                setIsLoading(false);
            }
        };

        fetchAnalytics();
    }, []);

    if (isLoading) {
        return (
            <div className="bg-amber-50 rounded-lg shadow-md p-6">
                <div className="flex items-center justify-center py-8">
                    <span className="loading loading-spinner loading-lg"></span>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-amber-50 rounded-lg shadow-md p-6">
                <Alert type="error" message={error} />
            </div>
        );
    }

    const { summary, lab_details, lab_type_distribution, capacity_issues } = analytics!;

    return (
        <div className="space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-amber-50 rounded-lg shadow-md p-4">
                    <div className="stat">
                        <div className="stat-title">Total Labs</div>
                        <div className="stat-value text-primary">{summary.total_labs}</div>
                    </div>
                </div>
                <div className="bg-amber-50 rounded-lg shadow-md p-4">
                    <div className="stat">
                        <div className="stat-title">Total Members</div>
                        <div className="stat-value text-secondary">{summary.total_members}</div>
                    </div>
                </div>
                <div className="bg-amber-50 rounded-lg shadow-md p-4">
                    <div className="stat">
                        <div className="stat-title">Total FTE</div>
                        <div className="stat-value text-accent">{summary.total_fte}</div>
                    </div>
                </div>
                <div className="bg-amber-50 rounded-lg shadow-md p-4">
                    <div className="stat">
                        <div className="stat-title">Avg Utilization</div>
                        <div className="stat-value text-info">
                            {(summary.average_utilization * 100).toFixed(1)}%
                        </div>
                    </div>
                </div>
            </div>

            {/* Lab Type Distribution */}
            <div className="bg-amber-50 rounded-lg shadow-md p-6">
                <h3 className="text-xl font-semibold mb-4">Lab Type Distribution</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {Object.entries(lab_type_distribution).map(([type, data]: [string, LabTypeData]) => (
                        <div key={type} className="border rounded-lg p-4">
                            <h4 className="font-medium text-lg mb-2">{type}</h4>
                            <div className="space-y-1 text-sm">
                                <div>Labs: {data.count}</div>
                                <div>Members: {data.total_members}</div>
                                <div>FTE: {data.total_fte}</div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Capacity Issues */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Over Capacity */}
                <div className="bg-amber-50 rounded-lg shadow-md p-6">
                    <h3 className="text-xl font-semibold mb-4 text-error">
                        Over Capacity Labs ({capacity_issues.over_capacity.length})
                    </h3>
                    {capacity_issues.over_capacity.length === 0 ? (
                        <p className="text-base-content/70">No labs over capacity</p>
                    ) : (
                        <div className="space-y-2">
                            {capacity_issues.over_capacity.map((lab: CapacityIssue) => (
                                <div key={lab.id} className="alert alert-error">
                                    <span className="font-medium">{lab.name}</span>
                                    <span>Excess: {lab.excess.toFixed(1)} FTE</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Under Utilized */}
                <div className="bg-amber-50 rounded-lg shadow-md p-6">
                    <h3 className="text-xl font-semibold mb-4 text-warning">
                        Under-Utilized Labs ({capacity_issues.under_utilized.length})
                    </h3>
                    {capacity_issues.under_utilized.length === 0 ? (
                        <p className="text-base-content/70">No under-utilized labs</p>
                    ) : (
                        <div className="space-y-2">
                            {capacity_issues.under_utilized.map((lab: UnderUtilizedLab) => (
                                <div key={lab.id} className="alert bg-amber-50 mb-4 border-2 border-solid border-[#eaddc9] rounded-lg shadow-md">
                                    <span className="font-medium">{lab.name}</span>
                                    <span>Utilization: {(lab.utilization * 100).toFixed(1)}%</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Detailed Lab Stats */}
            <div className="bg-amber-50 rounded-lg shadow-md p-6">
                <h3 className="text-xl font-semibold mb-4">Lab Details</h3>
                <div className="overflow-x-auto">
                    <table className="table table-zebra w-full">
                        <thead>
                            <tr>
                                <th>Lab Name</th>
                                <th>Type</th>
                                <th>Members</th>
                                <th>FTE</th>
                                <th>Utilization</th>
                                <th>Champion</th>
                            </tr>
                        </thead>
                        <tbody>
                            {lab_details.map((lab: UiLabDetail) => (
                                <tr key={lab.id}>
                                    <td className="font-medium">{lab.name}</td>
                                    <td>{lab.type}</td>
                                    <td>{lab.member_count}</td>
                                    <td>{lab.total_fte}</td>
                                    <td>
                                        <div className={`badge ${
                                            lab.utilization > 1 ? 'badge-error' :
                                            lab.utilization < 0.7 ? 'badge-warning' :
                                            'badge-success'
                                        }`}>
                                            {(lab.utilization * 100).toFixed(1)}%
                                        </div>
                                    </td>
                                    <td>{lab.champion_email || 'No champion'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};
